// api.js
import axios from 'axios';
import { toast } from 'react-toastify';
import { clearAuth } from '../utils/AuthConfig';

const { REACT_APP_API_URL } = process.env;

const api = axios.create({
  baseURL: REACT_APP_API_URL,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN'
});

let csrfPromise = null;

// Establish the CSRF cookie once. Laravel then refreshes the XSRF-TOKEN cookie
// on every response (including login, where the token rotates), so each request
// just reads the current cookie value — no resetting or retrying needed.
export const fetchCsrfCookie = () => {
  if (!csrfPromise) {
    csrfPromise = axios
      .get(`${REACT_APP_API_URL}/sanctum/csrf-cookie`, { withCredentials: true })
      .catch((err) => {
        csrfPromise = null;
        throw err;
      });
  }
  return csrfPromise;
};

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const readXsrfTokenFromCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

api.interceptors.request.use(
  async (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (MUTATING_METHODS.has(method)) {
      await fetchCsrfCookie();
      const token = readXsrfTokenFromCookie();
      if (token) {
        // eslint-disable-next-line no-param-reassign
        config.headers['X-XSRF-TOKEN'] = token;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const forceLogout = () => {
  clearAuth();
  csrfPromise = null;
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data, config } = error.response;
      const url = config?.url || '';
      const isLoginRequest = url.includes('/auth/login');

      if (status === 401 && !isLoginRequest) {
        forceLogout();
      } else if ([400, 403, 409, 429].includes(status) && data?.message) {
        toast.error(data.message);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
